import * as immutable from 'object-path-immutable';
import { defaultDataIdFromObject, useMutation } from '@apollo/client';
import { defineMessages, useIntl } from 'react-intl';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { ReactSortable } from 'react-sortablejs';
import styled from 'styled-components';
import { tokens } from '@companycam/slab';

import { ADMIN, MANAGER, hasRoleAccess } from 'helpers/roles';

import GET_TASK_LIST_TEMPLATE_HEADER_INFO from '../gql/getTaskListTemplateHeaderInfo.graphql';
import SUBTASK_TEMPLATE_CREATE from '../gql/subTaskTemplateCreate.graphql';
import SUBTASK_TEMPLATE_UPDATE from '../gql/subTaskTemplateUpdate.graphql';
import SUBTASK_TEMPLATE_DELETE from '../gql/subTaskTemplateDelete.graphql';
import {
  SUBTASK_UPDATE_POSITION,
  SUBTASK_TEMPLATE_UPDATE_POSITION,
} from './graphql/mutations.graphql';
import TASK_TEMPLATE_FRAGMENT from '../gql/TaskTemplateFragment.graphql';
import toast from 'helpers/toast';
import { useCurrentUser } from 'javascripts/hooks';
import useSubtaskCreate from './hooks/useSubtaskCreate';

import Drag from 'components/shared/DesignSystem/Drag';
import NewSubtaskDropdown from './NewSubtaskDropdown';
import SubtaskOptionsDropdown from './SubtaskOptionsDropdown';
import SubtaskForm from './SubtaskForm';
import { fireStevenEntityCrudEventForTemplate } from '../templates/templateUpdateStevenEvent';

const messages = defineMessages({
  dragIconTitle: {
    id: 'Project.taskList.subTasks.dragIconTitle',
    defaultMessage: 'Icon to drag and sort questions',
    description: 'used to describe drag icon image',
  },
  error: {
    id: 'Project.taskList.subTasks.updatePosition.error',
    defaultMessage: 'There was an error rearranging questions.',
    description: 'Update subtask position error message.',
  },
});

const FormContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  flex: 1;
`;

const SortableContainer = styled(ReactSortable)`
  width: 100%;
  /* margin-top: ${tokens.size_spacing_s}px; */
`;

const DragSortIcon = styled(Drag)`
  position: absolute;
  margin-left: 12px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'move')};
  opacity: 0.65;
  margin-top: 18px;

  &::after {
    content: '';
    position: absolute;
    left: -12px;
    width: 36px;
    height: 36px;
  }
`;

const SubtaskContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  cursor: default;
`;

const Index = ({
  isTemplate,
  isEditingTask,
  listId,
  task,
  subtasks,
  templateEditMode,
  hideCompletedTasks,
}) => {
  const { formatMessage } = useIntl();
  const user = useCurrentUser();
  const hasManagePermissions = hasRoleAccess(user, [ADMIN, MANAGER]);
  const [sortableSubtasks, setSortableSubtasks] = useState(subtasks);

  const isPreviewingTemplate = isTemplate && !templateEditMode;
  const { createSubtask } = useSubtaskCreate({ listId });

  const cacheUpdater = (constructorOptions) => (cacheOptions) => {
    const cacheId = defaultDataIdFromObject({
      __typename: constructorOptions.typeName,
      id: cacheOptions.id,
    });
    const cachedData = cacheOptions.cache.readFragment({
      id: cacheId,
      fragment: constructorOptions.fragment,
      fragmentName: constructorOptions.fragmentName,
    });
    const subtaskNodes = get(cachedData, constructorOptions.path, []);
    const updatedCacheData = (() => {
      switch (cacheOptions.operation) {
        case 'create':
          return [...subtaskNodes, cacheOptions.actionableItem];
        case 'delete':
          return subtaskNodes.filter(
            (subtask) => subtask.id !== cacheOptions.actionableItem,
          );
        default:
          return [];
      }
    })();

    const newQueryData = immutable.set(
      cachedData,
      constructorOptions.path,
      updatedCacheData,
    );
    cacheOptions.cache.writeFragment({
      id: cacheId,
      fragment: constructorOptions.fragment,
      fragmentName: constructorOptions.fragmentName,
      data: newQueryData,
    });
  };

  const cacheUpdateHandler = cacheUpdater({
    typeName: 'TaskTemplate',
    fragment: TASK_TEMPLATE_FRAGMENT,
    fragmentName: 'TaskTemplateFragment',
    path: 'subTaskTemplates.nodes',
  });

  const [subTaskTemplateCreate] = useMutation(SUBTASK_TEMPLATE_CREATE, {
    refetchQueries: [
      { query: GET_TASK_LIST_TEMPLATE_HEADER_INFO, variables: { id: listId } },
      'GetTaskListTemplateHeaderInfo',
    ],
    onCompleted: () => {
      fireStevenEntityCrudEventForTemplate({
        templateNodeId: listId,
        entityType: 'checklist_template_question',
        action: 'created',
      });
    },
    onError: () => {
      toast.error(
        formatMessage({ id: 'Projects.taskList.subTasks.createSubtask.error' }),
      );
    },
    update: (
      cache,
      {
        data: {
          subTaskTemplateCreate: { subTaskTemplate },
        },
      },
    ) => {
      cacheUpdateHandler({
        cache,
        operation: 'create',
        actionableItem: subTaskTemplate,
        id: task.id,
      });
    },
  });

  const [subTaskTemplateUpdate] = useMutation(SUBTASK_TEMPLATE_UPDATE, {
    refetchQueries: [
      { query: GET_TASK_LIST_TEMPLATE_HEADER_INFO, variables: { id: listId } },
      'GetTaskListTemplateHeaderInfo',
    ],
    onCompleted: () => {
      fireStevenEntityCrudEventForTemplate({
        templateNodeId: listId,
        entityType: 'checklist_template_question',
        action: 'updated',
      });
    },
    onError: () =>
      toast.error(formatMessage({ id: 'Project.taskList.subTasks.update' })),
  });

  const [subTaskTemplateDelete] = useMutation(SUBTASK_TEMPLATE_DELETE, {
    refetchQueries: [
      { query: GET_TASK_LIST_TEMPLATE_HEADER_INFO, variables: { id: listId } },
      'GetTaskListTemplateHeaderInfo',
    ],
    onCompleted: () => {
      fireStevenEntityCrudEventForTemplate({
        templateNodeId: listId,
        entityType: 'checklist_template_question',
        action: 'deleted',
      });
    },
    onError: () => {
      toast.error({ id: 'Projects.taskList.subTasks.deleteError' });
    },
    update: (cache, { data }) => {
      const {
        subTaskTemplateDelete: { deletedId },
      } = data;
      cacheUpdateHandler({
        cache,
        operation: 'delete',
        actionableItem: deletedId,
        id: task.id,
      });
    },
  });

  const [updateSubtaskPosition] = useMutation(SUBTASK_UPDATE_POSITION, {
    onError: () => toast.error(formatMessage(messages.error)),
  });
  const [updateSubtaskTemplatePosition] = useMutation(
    SUBTASK_TEMPLATE_UPDATE_POSITION,
    {
      onCompleted: () => {
        fireStevenEntityCrudEventForTemplate({
          templateNodeId: listId,
          entityType: 'checklist_template_question',
          action: 'updated',
        });
      },
      onError: () => toast.error(formatMessage(messages.error)),
      refetchQueries: [
        {
          query: GET_TASK_LIST_TEMPLATE_HEADER_INFO,
          variables: { id: listId },
        },
        'GetTaskListTemplateHeaderInfo',
      ],
    },
  );

  const handleReorderTemplateTasks = (newIndex, item) => {
    updateSubtaskTemplatePosition({
      variables: {
        input: {
          id: item.getAttribute('data-id'),
          position: newIndex,
        },
      },
    });
  };
  const handleOnEnd = ({ newIndex, item }) => {
    if (isTemplate) {
      handleReorderTemplateTasks(newIndex, item);
    } else {
      updateSubtaskPosition({
        variables: {
          input: {
            id: item.getAttribute('data-id'),
            position: newIndex,
          },
        },
      });
    }
  };

  const handleAddSubtaskTemplate = (subtaskType) => {
    subTaskTemplateCreate({
      variables: {
        input: {
          label: '',
          answerType: subtaskType,
          taskTemplateId: task.id,
        },
      },
    });
  };

  const handleAddSubtask = (subtaskType) => {
    if (isTemplate) {
      handleAddSubtaskTemplate(subtaskType);
    } else {
      createSubtask({
        variables: {
          input: {
            label: '',
            answerType: subtaskType,
            taskId: task.id,
          },
        },
      });
    }
  };

  useEffect(() => setSortableSubtasks(subtasks), [subtasks]);

  return (
    <>
      <SortableContainer
        animation={200}
        handle=".subtask-sort-handle"
        list={sortableSubtasks}
        group={`${task.id}-subtask`}
        onEnd={handleOnEnd}
        setList={setSortableSubtasks}
        completed={task.completed}
      >
        {sortableSubtasks.map((subtask) => (
          <SubtaskContainer key={subtask.id}>
            {isEditingTask ? (
              <DragSortIcon
                accessibilityLabel={formatMessage(messages.dragIconTitle)}
                className="subtask-sort-handle"
                color={tokens.color_text_nonessential_le}
                size="small"
              />
            ) : null}

            <FormContainer>
              <SubtaskForm
                listId={listId}
                isEditingTask={isEditingTask}
                isTemplate={isTemplate}
                task={task}
                subtask={subtask}
                updateSubtaskTemplate={subTaskTemplateUpdate}
                hideCompletedTasks={hideCompletedTasks}
              />
            </FormContainer>
            {hasManagePermissions && !isPreviewingTemplate && isEditingTask ? (
              <SubtaskOptionsDropdown
                isTemplate={isTemplate}
                subtaskId={subtask.id}
                task={task}
                deleteSubtaskTemplate={subTaskTemplateDelete}
              />
            ) : null}
          </SubtaskContainer>
        ))}
      </SortableContainer>
      {isEditingTask ? (
        <NewSubtaskDropdown
          handleAddSubtask={handleAddSubtask}
          taskId={task.id}
        />
      ) : null}
    </>
  );
};

Index.propTypes = {
  isTemplate: PropTypes.bool.isRequired,
  isEditingTask: PropTypes.bool.isRequired,
  listId: PropTypes.string.isRequired,
  subtasks: PropTypes.array.isRequired,
  task: PropTypes.shape({
    id: PropTypes.string,
    completed: PropTypes.bool,
  }),
  templateEditMode: PropTypes.bool,
  hideCompletedTasks: PropTypes.bool,
};

export default Index;
